import React, { useState, useRef, useCallback } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import P, { InferProps } from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import DeviceColumn from './column-devices';
import StaticColumn from './column-static';
import { Module } from './types';
import { isAllFieldsValid } from '../../../utils/team';
import { Box } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

// reordering the result
const reorder = (list: Module[], startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// moves an item from one list to another list
const copy = (
  source: Module[],
  destination: Module[],
  droppableSource: { droppableId: string; index: number },
  droppableDestination: { droppableId: string; index: number }
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const newModule = cloneDeep(sourceClone[droppableSource.index]);

  destClone.splice(droppableDestination.index, 0, {
    ...newModule,
    id: uuidv4(),
  });
  return destClone;
};

const move = (
  source: Module[],
  destination: Module[],
  droppableSource: { droppableId: string; index: number },
  droppableDestination: { droppableId: string; index: number }
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {
    [droppableSource.droppableId]: sourceClone,
    [droppableDestination.droppableId]: destClone,
  };

  return result;
};

function DragDropContainer({
  setDevices,
  selectedDevices,
  sceneModules,
  modules,
  updateDeviceSceneConfig,
  isReadOnly,
  pathParams,
  onValidation,
  promos,
}: InferProps<typeof DragDropContainer.propTypes>) {
  const editedFields = useRef<{ [key: string]: boolean }>({});
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const has3Columns = useMediaQuery('(min-width:1400px)');
  const has2Columns = useMediaQuery('(min-width:1010px)');
  const has1Column = !has3Columns && !has2Columns;
  type ModuleDeviceTypes = keyof typeof modules;

  const setChange = (key: string, value: string) => {
    const newModules = {
      mobile: Array.from(modules.mobile),
      tablet: Array.from(modules.tablet),
    };

    set(newModules, key, value);
    updateDeviceSceneConfig(newModules);
  };

  const setValidation = useCallback(
    async (fieldKey: string, value: string | number | boolean, fieldValidation: any) => {
      const isCurrentFieldValid: boolean = await fieldValidation.isValid(value);
      editedFields.current[fieldKey] = isCurrentFieldValid;
      const isFieldValid = isAllFieldsValid(editedFields.current);

      onValidation(isFieldValid);
    },
    [onValidation]
  );

  const handleDelete = (key: keyof typeof modules, index: number) => {
    const modulesClone = cloneDeep(modules);

    modulesClone[key].splice(index, 1);

    updateDeviceSceneConfig(modulesClone);
  };

  function togglePreview(value: boolean) {
    setShowPreview(value);
  }

  const handleOnDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (!sceneModules) {
      return;
    }

    // dropped outside the column
    if (!destination) {
      return;
    }
    if (window.navigator.vibrate) {
      window.navigator.vibrate(100);
    }

    switch (source.droppableId) {
      case destination.droppableId:
        updateDeviceSceneConfig({
          ...modules,
          [destination.droppableId]: reorder(
            modules[source.droppableId as ModuleDeviceTypes],
            source.index,
            destination.index
          ),
        });
        break;
      case 'MODULES':
        updateDeviceSceneConfig({
          ...modules,
          [destination.droppableId]: copy(
            sceneModules,
            modules[destination.droppableId as ModuleDeviceTypes],
            source,
            destination
          ),
        });
        break;
      default:
        updateDeviceSceneConfig({
          ...modules,
          ...move(
            modules[source.droppableId as ModuleDeviceTypes],
            modules[destination.droppableId as ModuleDeviceTypes],
            source,
            destination
          ),
        });
        break;
    }
  };

  const [openStaticModulesDrawer, setOpenStaticModulesDrawer] = React.useState(false);

  return (
    <DragDropContext onDragEnd={handleOnDragEnd}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          [theme.breakpoints.up('sm')]: {
            height: '100%',
          },
        })}
      >
        <StaticColumn
          staticModules={sceneModules}
          togglePreview={togglePreview}
          setDevices={setDevices}
          selectedDevices={selectedDevices}
          isReadOnly={isReadOnly}
          openStaticModulesDrawer={openStaticModulesDrawer}
          setOpenStaticModulesDrawer={setOpenStaticModulesDrawer}
          has1Column={has1Column}
          addModule={(selectedId) => {
            updateDeviceSceneConfig({
              ...modules,
              [selectedDevices]: copy(
                sceneModules,
                modules[selectedDevices as ModuleDeviceTypes],
                {
                  droppableId: 'MODULES',
                  index: sceneModules.findIndex((m: Module) => m.id === selectedId),
                },
                {
                  droppableId: selectedDevices,
                  index: 0,
                }
              ),
            });
          }}
        />
        <DeviceColumn
          modules={modules}
          selectedDevices={selectedDevices}
          setChange={setChange}
          setValidation={setValidation}
          showPreview={showPreview}
          togglePreview={togglePreview}
          onDelete={handleDelete}
          isReadOnly={isReadOnly}
          pathParams={pathParams}
          promos={promos}
          has3Columns={has3Columns}
          has2Columns={has2Columns}
          has1Column={has1Column}
          openStaticModulesDrawer={openStaticModulesDrawer}
          setOpenStaticModulesDrawer={setOpenStaticModulesDrawer}
        />
      </Box>
    </DragDropContext>
  );
}

DragDropContainer.propTypes = {
  selectedDevices: P.string.isRequired,
  setDevices: P.func.isRequired,
  sceneModules: P.any.isRequired,
  modules: P.shape({
    mobile: P.any.isRequired,
    tablet: P.any.isRequired,
  }).isRequired,
  updateDeviceSceneConfig: P.func.isRequired,
  isReadOnly: P.bool.isRequired,
  pathParams: P.string.isRequired,
  onValidation: P.func.isRequired,
  promos: P.arrayOf(P.object),
};

export default DragDropContainer;
